import {throttle} from '@northpoint-development/labs-common-core';
import {insertAuditRecord} from './BackendService';
import {currentUser} from './FirestoreService';
import {callProcedure} from './BigQueryService';

/**
 * @typedef {Object} LocalMetric Local metric data for a user.
 * @property {string} metricId The metric this data pertains to.
 * @property {number} session The amount for this metric attributable to the current session.
 * @property {number} pending The amount for this metric pending to be persisted to BigQuery.
 */

/** @type {Function} @private */
const throttledUpdateMetrics = throttle(updateMetrics);

/** @type {Map<MetricId, LocalMetric>} */
const localMetrics = new Map();

/**
 * Fetches the current month's usage of a tile type.
 * @async
 * @param {string} eventType The type of tile to fetch usage for.
 * @return {Promise<number>} A promise that resolves with the usage count.
 */
const getTileUsage = async (eventType) => {
  const currentDate = new Date().toISOString().split('-');
  const currentYear = currentDate[0];
  const currentMonth = currentDate[1];
  try {
    return (await callProcedure('UsageStatistics', 'getTileUsage', [
      currentUser().uid,
      eventType,
      currentYear,
      currentMonth,
    ]))[0].tiles;
  } catch (error) {
    error.message = `Error fetching current month usage for ${eventType} tiles`;
    console.warn(error);
    return 0;
  }
};

/**
 * Fetches the current month's usage of all tile types for all users.
 * @async
 * @return {Promise<object>} A promise that resolves with the usage count for all tile types.
 */
const getTotalTileUsage = async () => {
  const currentDate = new Date().toISOString().split('-');
  const currentYear = currentDate[0];
  const currentMonth = currentDate[1];
  try {
    const usage = await callProcedure('UsageStatistics', 'getTotalTileUsage', [
      null, // leave the user id null to get the total usage
      currentYear,
      currentMonth,
    ]);
    if (!usage) {
      throw new Error('No usage data found');
    }
    return usage;
  } catch (error) {
    error.message = 'Error fetching layer usage: ' + error.message;
    console.error(error);
    return null;
  }
};

/**
 * Returns the amount of a metric captured for a chosen user this session.
 * @param {MetricId} metricId The ID of the metric to check.
 * @return {number} The amount of the metric captured during this session.
 */
function getSessionMetric(metricId) {
  try {
    return localMetrics.get(metricId)?.session ?? 0;
  } catch (error) {
    error.message = 'Failed to get session metric: ' + error.message;
    console.error(error);
  }
}

/**
 * Inserts a user metric record into BigQuery.
 * @async
 * @param {LocalMetric} localMetric The metric to insert.
 */
async function insertUserMetric(localMetric) {
  const sent = localMetric.pending;
  if (sent === 0) return;
  // Zeroed before the write so increments that land during it count toward the next one.
  localMetric.pending = 0;
  try {
    // insertAuditRecord throws on failure; createAuditRecord would swallow it.
    await insertAuditRecord({eventType: localMetric.metricId, tilesFetched: sent});
  } catch (err) {
    // Put back only what this write carried.
    localMetric.pending += sent;
    err.message = `Failed to update user metrics: ${err.message ?? err}`;
    console.error(err);
  }
}

/**
 * Updates BigQuery with new user metric values from this service.
 * Call directly only when this needs to happen urgently. Calls to this function are
 * otherwise throttled whenever metrics are updated locally.
 * @async
 */
async function updateMetrics() {
  try {
    /** @type {Promise<void>[]} */
    const userUpdates = [];
    localMetrics.forEach((localMetric) => {
      userUpdates.push(insertUserMetric(localMetric));
    });
    await Promise.all(userUpdates);
  } catch (error) {
    error.message = 'Failed to update metrics: ' + error.message;
    console.error(error);
  }
}

/**
 * Increases a chosen metric for a user by a chosen amount.
 * @param {MetricId} metricId The ID of the metric to increase.
 * @param {number} amount The amount by which to increase the metric. Defaults to 1.
 */
function increaseMetric(metricId, amount = 1) {
  try {
    if (!metricId || !amount) return;
    if (!currentUser()?.uid) {
      console.warn(`Unable to track metric ${metricId}: user is unauthorized.`);
      return;
    }
    if (!localMetrics.has(metricId)) {
      localMetrics.set(metricId, {metricId, session: amount, pending: amount});
    } else {
      const localMetric = localMetrics.get(metricId);
      localMetric.session += amount;
      localMetric.pending += amount;
    }
    throttledUpdateMetrics();
  } catch (error) {
    error.message = 'Failed to increase metric: ' + error.message;
    console.error(error);
  }
}

const MetricService = {
  getTileUsage,
  getTotalTileUsage,
  getSessionMetric,
  updateMetrics,
  increaseMetric,
};

export {
  MetricService,
};
